import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { CheckedState } from '@radix-ui/react-checkbox';
import { Modal } from '@/components/modal';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import HelpTooltip from '@/components/ui/help-tooltip';
import { Badge } from '@/components/ui/badge';
import { useNotification } from '@/components/notifications';
// import {
//   Select,
//   SelectContent,
//   SelectItem,
//   SelectTrigger,
//   SelectValue,
// } from '@/components/ui/select';
import type {
  CSVQuality,
  Dataset,
  DatasetCsvForeignKey,
  DatasetCsvForeignKeyReference,
  DatasetCsvReference,
  DatasetCsvUploadMetadata,
  QualityIssue,
} from '@/types/source-types';
import {
  DatasetCsvKeyEditor,
  DatasetCsvSelection,
  DatasetCsvSelectionError,
  DatasetOption,
  DatasetCsvForeignKeyError,
  buildDefaultSelection,
  createDatasetForeignKeyId,
} from './dataset-csv-key-editor';
import { getUploadMetadata } from '@/services/data-sources/get-upload-metadata';
import { cn } from '@/lib/utils';
import { CsvQualityResolver } from '@/features/data-quality/csv-quality-resolver';
import {
  MetadataResolutionPayload,
  submitMetadataResolutions,
} from '@/services/data-sources/submit-metadata-resolutions';

export interface DatasetFormValues {
  name: string;
  description?: string;
  csv_object_keys: DatasetCsvReference[];
}

interface CsvOption {
  key: string;
  label: string;
  sizeMb?: number | null;
  quality?: CSVQuality | null;
}

interface MetadataState {
  status: 'idle' | 'loading' | 'success' | 'error';
  metadata: DatasetCsvUploadMetadata | null;
  error?: string;
}

interface DatasetModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  initialValues?: DatasetFormValues | null;
  csvOptions: CsvOption[];
  datasets: Dataset[];
  collectionId: string;
  activeDatasetId?: string;
  focusKey?: string | null;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (values: DatasetFormValues) => Promise<void>;
  onRefreshSources?: () => Promise<void>;
}

const normalizeToStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === 'string' ? item.trim() : ''))
      .filter((item): item is string => Boolean(item));
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? [trimmed] : [];
  }

  return [];
};

const mapReferenceToSelection = (
  reference: DatasetCsvReference,
): DatasetCsvSelection => {
  const foreignKeys = Array.isArray(reference?.foreign_keys)
    ? reference.foreign_keys.map((fk) => {
        const columns = normalizeToStringArray(fk?.columns);
        const referencedColumns = normalizeToStringArray(
          fk?.references?.columns,
        );
        const datasetId =
          typeof fk?.references?.dataset_id === 'string'
            ? fk.references.dataset_id
            : undefined;
        const table =
          typeof fk?.references?.table === 'string' && fk.references.table
            ? fk.references.table
            : undefined;

        return {
          id: createDatasetForeignKeyId(),
          columns,
          datasetId,
          table,
          referencedColumns,
        };
      })
    : [];

  return {
    key: reference.s3_key,
    primaryKey: normalizeToStringArray(reference?.primary_key),
    foreignKeys,
  };
};

const derivePrimaryKeySuggestions = (
  metadata: DatasetCsvUploadMetadata | null,
) => {
  if (!metadata) return [] as string[];
  const suggestions =
    metadata.primary_key_suggestions ||
    metadata.primary_key ||
    metadata.suggested_primary_keys;
  return Array.isArray(suggestions)
    ? suggestions.filter((value): value is string => typeof value === 'string')
    : [];
};

export const DatasetModal = ({
  isOpen,
  mode,
  initialValues,
  csvOptions,
  datasets,
  collectionId,
  activeDatasetId,
  focusKey,
  isSubmitting,
  onClose,
  onSubmit,
  onRefreshSources,
}: DatasetModalProps) => {
  const notification = useNotification();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [selections, setSelections] = useState<
    Record<string, DatasetCsvSelection>
  >({});
  const [metadataByKey, setMetadataByKey] = useState<
    Record<string, MetadataState>
  >({});
  const [qualitySavingKeys, setQualitySavingKeys] = useState<
    Record<string, boolean>
  >({});
  const [validationErrors, setValidationErrors] = useState<
    Record<string, DatasetCsvSelectionError>
  >({});
  const [nameError, setNameError] = useState('');
  const [csvSelectionError, setCsvSelectionError] = useState('');
  const [filterTerm, setFilterTerm] = useState('');
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [lastAppliedKey, setLastAppliedKey] = useState<string | null>(null);

  const existingReferenceMap = useMemo(() => {
    const map = new Map<string, DatasetCsvReference>();
    (initialValues?.csv_object_keys ?? []).forEach((reference) => {
      if (reference?.s3_key) {
        map.set(reference.s3_key, reference);
      }
    });
    return map;
  }, [initialValues?.csv_object_keys]);

  const existingSelectionMap = useMemo(() => {
    const entries: Record<string, DatasetCsvSelection> = {};
    (initialValues?.csv_object_keys ?? []).forEach((reference) => {
      if (reference?.s3_key) {
        entries[reference.s3_key] = mapReferenceToSelection(reference);
      }
    });
    return entries;
  }, [initialValues?.csv_object_keys]);

  useEffect(() => {
    if (!isOpen) {
      setActiveKey(null);
      setQualitySavingKeys({});
      return;
    }

    setName(initialValues?.name ?? '');
    setDescription(initialValues?.description ?? '');
    const initialKeys = (initialValues?.csv_object_keys ?? [])
      .map((reference) => reference?.s3_key)
      .filter((key): key is string => Boolean(key));
    setSelectedKeys(initialKeys);
    setSelections(existingSelectionMap);
    setValidationErrors({});
    setNameError('');
    setCsvSelectionError('');
    setFilterTerm('');
    setLastAppliedKey(null);
    const preferredKey =
      focusKey && initialKeys.includes(focusKey)
        ? focusKey
        : (initialKeys[0] ?? null);
    setActiveKey(preferredKey);
  }, [existingSelectionMap, focusKey, initialValues, isOpen]);

  const selectedKeysSet = useMemo(() => new Set(selectedKeys), [selectedKeys]);

  const csvLabelLookup = useMemo(() => {
    const map = new Map<string, string>();
    csvOptions.forEach((option) => {
      if (option.key) {
        map.set(option.key, option.label);
      }
    });
    return map;
  }, [csvOptions]);

  const qualityByKey = useMemo(() => {
    const map = new Map<string, CSVQuality | null | undefined>();
    csvOptions.forEach((option) => {
      if (option.key) {
        map.set(option.key, option.quality);
      }
    });
    return map;
  }, [csvOptions]);

  const unresolvedQualityKeys = useMemo(() => {
    return selectedKeys.filter((key) => {
      const metadata = metadataByKey[key]?.metadata;
      if (metadata?.quality) {
        return Boolean(metadata.quality.requires_resolution);
      }

      const initialQuality = qualityByKey.get(key);
      return Boolean(initialQuality?.requires_resolution);
    });
  }, [metadataByKey, qualityByKey, selectedKeys]);

  const unresolvedQualityNames = useMemo(() => {
    return unresolvedQualityKeys.map((key) => {
      const label = csvLabelLookup.get(key);
      if (label) return label;
      const filename = key.split('/').pop();
      return filename || key;
    });
  }, [csvLabelLookup, unresolvedQualityKeys]);

  const isSavingQuality = useMemo(
    () => Object.values(qualitySavingKeys).some(Boolean),
    [qualitySavingKeys],
  );

  const hasUnresolvedQuality = unresolvedQualityKeys.length > 0;

  useEffect(() => {
    if (!selectedKeys.length) {
      setActiveKey(null);
      return;
    }

    setActiveKey((current) =>
      current && selectedKeys.includes(current) ? current : selectedKeys[0],
    );
  }, [selectedKeys]);

  useEffect(() => {
    setLastAppliedKey(null);
  }, [selectedKeys]);

  const addSelectionIfMissing = useCallback(
    (key: string) => {
      setSelections((prev) => {
        if (prev[key]) {
          return prev;
        }
        const existing = existingSelectionMap[key];
        return {
          ...prev,
          [key]: existing ? existing : buildDefaultSelection(key),
        };
      });
    },
    [existingSelectionMap],
  );

  const handleToggle = (key: string, checked: boolean) => {
    setSelectedKeys((prev) => {
      if (checked) {
        if (prev.includes(key)) {
          return prev;
        }
        return [...prev, key];
      }
      return prev.filter((item) => item !== key);
    });

    if (checked) {
      addSelectionIfMissing(key);
      if (csvSelectionError) setCsvSelectionError('');
    }
  };

  const updateSelection = useCallback(
    (key: string, next: DatasetCsvSelection) => {
      setSelections((prev) => ({
        ...prev,
        [key]: next,
      }));
      setValidationErrors((prev) => {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setLastAppliedKey(null);
    },
    [],
  );

  const setMetadataState = useCallback((key: string, state: MetadataState) => {
    setMetadataByKey((prev) => ({
      ...prev,
      [key]: state,
    }));
  }, []);

  const fetchMetadata = useCallback(
    async (key: string) => {
      setMetadataByKey((prev) => {
        const current = prev[key];
        if (current?.status === 'loading') {
          return prev;
        }
        return {
          ...prev,
          [key]: {
            status: 'loading',
            metadata: current?.metadata ?? null,
          },
        };
      });

      const result = await getUploadMetadata(collectionId, key);

      if ('detail' in result) {
        setMetadataState(key, {
          status: 'error',
          metadata: null,
          error: result.detail,
        });
        return;
      }

      const metadata = result.metadata ?? null;
      setMetadataState(key, {
        status: 'success',
        metadata,
      });

      if (!metadata) return;

      setSelections((prev) => {
        const current = prev[key] ?? buildDefaultSelection(key);
        if (current.primaryKey.length) {
          return prev;
        }

        const suggestions = derivePrimaryKeySuggestions(metadata);
        const headers = Array.isArray(metadata.headers)
          ? metadata.headers.filter(
              (value): value is string => typeof value === 'string',
            )
          : [];
        const validSuggestions = suggestions.filter((column) =>
          headers.includes(column),
        );

        if (!validSuggestions.length) {
          return {
            ...prev,
            [key]: current,
          };
        }

        return {
          ...prev,
          [key]: {
            ...current,
            primaryKey: validSuggestions,
          },
        };
      });
    },
    [collectionId, setMetadataState],
  );

  useEffect(() => {
    selectedKeys.forEach((key) => {
      const state = metadataByKey[key];
      if (!state || state.status === 'idle') {
        fetchMetadata(key);
      }
    });
  }, [fetchMetadata, metadataByKey, selectedKeys]);

  const handleSelectAllFiltered = useCallback(
    (checked: boolean, keys: string[]) => {
      setSelectedKeys((prev) => {
        const next = new Set(prev);

        if (checked) {
          keys.forEach((key) => next.add(key));
        } else {
          keys.forEach((key) => next.delete(key));
        }

        return Array.from(next);
      });

      if (checked) {
        keys.forEach((key) => {
          addSelectionIfMissing(key);
        });
        if (csvSelectionError) setCsvSelectionError('');
      }
    },
    [addSelectionIfMissing, csvSelectionError],
  );

  const handleSelectionChange = useCallback(
    (key: string, next: DatasetCsvSelection) => {
      updateSelection(key, next);
    },
    [updateSelection],
  );

  const handleQualitySubmit = useCallback(
    async (key: string, resolutions: MetadataResolutionPayload[]) => {
      setQualitySavingKeys((prev) => ({ ...prev, [key]: true }));

      try {
        const result = await submitMetadataResolutions(
          collectionId,
          key,
          resolutions,
        );

        if ('detail' in result) {
          throw new Error(
            result.detail || 'Failed to save data quality fixes.',
          );
        }

        setMetadataState(key, {
          status: 'success',
          metadata: result.metadata ?? null,
        });

        if (onRefreshSources) {
          try {
            await onRefreshSources();
          } catch (refreshError) {
            console.error(
              'Failed to refresh collection sources:',
              refreshError,
            );
          }
        }

        notification.success(
          'Data quality fixes saved. Schema sync will now use the updated headers.',
        );
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : 'Failed to save data quality fixes.';
        notification.error(message);
        throw error instanceof Error ? error : new Error(message);
      } finally {
        setQualitySavingKeys((prev) => ({ ...prev, [key]: false }));
      }
    },
    [collectionId, notification, onRefreshSources, setMetadataState],
  );

  const cloneSelectionForKey = useCallback(
    (targetKey: string, source: DatasetCsvSelection, preserveIds = false) => {
      return {
        key: targetKey,
        primaryKey: [...source.primaryKey],
        foreignKeys: source.foreignKeys.map((fk) => ({
          ...fk,
          id: preserveIds ? fk.id : createDatasetForeignKeyId(),
          columns: [...fk.columns],
          referencedColumns: [...fk.referencedColumns],
        })),
      };
    },
    [],
  );

  const handleApplySelectionToAll = useCallback(
    (selection: DatasetCsvSelection) => {
      setSelections((prev) => {
        const next = { ...prev };
        selectedKeys.forEach((key) => {
          next[key] = cloneSelectionForKey(
            key,
            selection,
            key === selection.key,
          );
        });
        return next;
      });
      setValidationErrors({});
      setLastAppliedKey(selection.key);
    },
    [cloneSelectionForKey, selectedKeys],
  );

  const formatSize = useCallback((sizeMb?: number | null) => {
    if (sizeMb == null || Number.isNaN(sizeMb)) {
      return null;
    }

    const bytes = sizeMb * 1024 * 1024;
    if (bytes < 1024) {
      return `${bytes.toFixed(0)} B`;
    }

    const units = ['KB', 'MB', 'GB', 'TB'];
    let value = bytes;
    let unitIndex = -1;

    do {
      value /= 1024;
      unitIndex += 1;
    } while (value >= 1024 && unitIndex < units.length - 1);

    return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unitIndex]}`;
  }, []);

  const missingOptions = useMemo(() => {
    const existingKeys = new Set(csvOptions.map((option) => option.key));
    return Array.from(existingReferenceMap.keys())
      .filter((key): key is string => Boolean(key))
      .filter((key) => !existingKeys.has(key))
      .map((key) => ({
        key,
        label: `${key.split('/').pop() || key} (missing)`,
        sizeMb: null,
        quality: null,
      }));
  }, [csvOptions, existingReferenceMap]);

  const allOptions = useMemo(
    () => [...csvOptions, ...missingOptions],
    [csvOptions, missingOptions],
  );

  const optionLabelLookup = useMemo(() => {
    const map = new Map<string, string>();
    allOptions.forEach((option) => {
      map.set(option.key, option.label);
    });
    return map;
  }, [allOptions]);

  const sortedOptions = useMemo(
    () => [...allOptions].sort((a, b) => a.label.localeCompare(b.label)),
    [allOptions],
  );

  const filteredOptions = useMemo(() => {
    const query = filterTerm.trim().toLowerCase();
    if (!query) {
      return sortedOptions;
    }

    return sortedOptions.filter((option) =>
      option.label.toLowerCase().includes(query),
    );
  }, [filterTerm, sortedOptions]);

  const filteredOptionKeys = useMemo(
    () => filteredOptions.map((option) => option.key),
    [filteredOptions],
  );

  const selectedFilteredCount = useMemo(() => {
    if (!filteredOptionKeys.length) {
      return 0;
    }

    return filteredOptionKeys.reduce(
      (count, key) => (selectedKeysSet.has(key) ? count + 1 : count),
      0,
    );
  }, [filteredOptionKeys, selectedKeysSet]);

  const selectAllState: CheckedState = filteredOptionKeys.length
    ? selectedFilteredCount === filteredOptionKeys.length
      ? true
      : selectedFilteredCount > 0
        ? 'indeterminate'
        : false
    : false;

  const datasetOptions = useMemo<DatasetOption[]>(() => {
    const optionMap = new Map<string, string>();
    datasets.forEach((dataset) => {
      optionMap.set(dataset.id, dataset.name);
    });
    Object.values(selections).forEach((selection) => {
      selection.foreignKeys.forEach((fk) => {
        if (fk.datasetId && !optionMap.has(fk.datasetId)) {
          optionMap.set(fk.datasetId, fk.datasetId);
        }
      });
    });
    if (activeDatasetId && !optionMap.has(activeDatasetId)) {
      optionMap.set(activeDatasetId, activeDatasetId);
    }
    return Array.from(optionMap.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [activeDatasetId, datasets, selections]);

  // const selectedKeyOptions = useMemo(
  //   () =>
  //     selectedKeys.map((key) => ({
  //       value: key,
  //       label: optionLabelLookup.get(key) || key.split('/').pop() || key,
  //     })),
  //   [optionLabelLookup, selectedKeys],
  // );

  const activeSelection = activeKey ? (selections[activeKey] ?? null) : null;
  const activeMetadataState = activeKey ? metadataByKey[activeKey] : undefined;
  const activeMetadata = activeMetadataState?.metadata ?? null;
  const activeMetadataError =
    activeMetadataState?.status === 'error'
      ? activeMetadataState.error
      : undefined;
  const activeIsLoading = activeMetadataState?.status === 'loading';
  const activeDisplayName = activeKey
    ? optionLabelLookup.get(activeKey) ||
      activeKey.split('/').pop() ||
      activeKey
    : '';

  const handleSubmit = async () => {
    // Clear all errors first
    setNameError('');
    setCsvSelectionError('');

    if (!name.trim()) {
      setNameError('Dataset name is required');
      return;
    }

    if (!selectedKeys.length) {
      setCsvSelectionError('Select at least one CSV file');
      return;
    }

    if (hasUnresolvedQuality) {
      notification.warning(
        'Resolve outstanding data-quality issues before creating this dataset.',
      );
      return;
    }

    if (isSavingQuality) {
      notification.info('Please wait for data-quality fixes to finish saving.');
      return;
    }

    const nextErrors: Record<string, DatasetCsvSelectionError> = {};
    let hasErrors = false;

    selectedKeys.forEach((key) => {
      const selection = selections[key] ?? buildDefaultSelection(key);
      const foreignKeyErrors = selection.foreignKeys
        .map((fk) => {
          const fkErrors: DatasetCsvForeignKeyError = {
            id: fk.id,
          };

          if (!fk.columns.length) {
            fkErrors.columns = 'Select at least one local column';
          }

          if (!fk.datasetId) {
            fkErrors.datasetId = 'Select a dataset to reference';
          }

          if (!fk.referencedColumns.length) {
            fkErrors.referencedColumns = 'Enter referenced column name(s)';
          }

          const hasForeignKeyErrors =
            fkErrors.columns ||
            fkErrors.datasetId ||
            fkErrors.referencedColumns;

          return hasForeignKeyErrors ? fkErrors : null;
        })
        .filter((value): value is DatasetCsvForeignKeyError => Boolean(value));

      if (foreignKeyErrors.length) {
        hasErrors = true;
        nextErrors[key] = {
          ...(nextErrors[key] || {}),
          foreignKeys: foreignKeyErrors,
        };
      }
    });

    if (hasErrors) {
      setValidationErrors(nextErrors);
      return;
    }

    setValidationErrors({});

    const payloadReferences: DatasetCsvReference[] = selectedKeys.map((key) => {
      const selection = selections[key] ?? buildDefaultSelection(key);
      const existing = existingReferenceMap.get(key);
      const reference: DatasetCsvReference = {
        ...(existing || {}),
        s3_key: key,
      };

      if (selection.primaryKey.length) {
        reference.primary_key = selection.primaryKey;
      } else {
        delete reference.primary_key;
      }

      if (selection.foreignKeys.length) {
        const foreignKeys: DatasetCsvForeignKey[] = selection.foreignKeys
          .map((fk) => {
            if (!fk.columns.length || !fk.referencedColumns.length) {
              return null;
            }

            const references: DatasetCsvForeignKeyReference = {};
            if (fk.datasetId) {
              references.dataset_id = fk.datasetId;
            }
            if (fk.table) {
              references.table = fk.table;
            }
            if (fk.referencedColumns.length) {
              references.columns = fk.referencedColumns;
            }

            const nextForeignKey: DatasetCsvForeignKey = {
              columns: fk.columns,
            };

            if (Object.keys(references).length) {
              nextForeignKey.references = references;
            }

            return nextForeignKey;
          })
          .filter((fk): fk is DatasetCsvForeignKey => Boolean(fk));

        if (foreignKeys.length) {
          reference.foreign_keys = foreignKeys;
        } else {
          delete reference.foreign_keys;
        }
      } else {
        delete reference.foreign_keys;
      }

      return reference;
    });

    await onSubmit({
      name: name.trim(),
      description: description.trim() ? description.trim() : undefined,
      csv_object_keys: payloadReferences,
    });
  };

  const disableSubmit = isSubmitting || hasUnresolvedQuality || isSavingQuality;
  const footerMessage = isSavingQuality
    ? 'Saving data-quality fixes…'
    : hasUnresolvedQuality
      ? 'Resolve outstanding data-quality issues before continuing.'
      : null;

  return (
    <Modal
      isDialogOpen={isOpen}
      setIsDialogOpen={(open) => {
        if (!open) {
          onClose();
        }
      }}
      contentClassName='sm:max-w-[56rem] md:min-w-[720px] flex flex-col max-h-[85vh]'
      title={
        <span>{mode === 'create' ? 'Create dataset' : 'Edit dataset'}</span>
      }
      footer={
        <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
          {footerMessage ? (
            <div className='flex items-center gap-2 text-xs text-amber-700'>
              <AlertTriangle className='h-3.5 w-3.5 shrink-0' />
              <span>{footerMessage}</span>
            </div>
          ) : (
            <span className='sr-only'>Ready to submit</span>
          )}
          <div className='flex justify-end gap-2 sm:ml-auto'>
            <Button variant='outline' onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={disableSubmit}>
              {mode === 'create' ? 'Create' : 'Save changes'}
            </Button>
          </div>
        </div>
      }
    >
      <div className='flex flex-col gap-4 overflow-y-auto flex-1 px-1'>
        <div className='space-y-2'>
          <div className='flex items-center gap-2'>
            <Label className='label-required' htmlFor='dataset-name'>
              Dataset name
            </Label>
            <HelpTooltip label='Dataset title help'>
              <p>
                Give the dataset a clear, human-friendly name. This is what
                teammates see in the dataset list.
              </p>
            </HelpTooltip>
          </div>
          <Input
            id='dataset-name'
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              if (nameError) setNameError('');
            }}
            placeholder='Enter dataset name'
            disabled={isSubmitting}
            className={cn(nameError && 'border-red-500')}
            autoFocus
          />
          {nameError && <p className='text-sm text-red-500'>{nameError}</p>}
        </div>

        <div className='space-y-2'>
          <div className='flex items-center gap-2'>
            <Label htmlFor='dataset-description'>Description</Label>
            <HelpTooltip label='Dataset notes help'>
              <p>
                Optional notes that explain what the dataset contains or how it
                should be used.
              </p>
            </HelpTooltip>
          </div>
          <Textarea
            id='dataset-description'
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder='Describe the dataset (optional)'
            disabled={isSubmitting}
            className='min-h-[80px]'
          />
        </div>

        {hasUnresolvedQuality && (
          <div className='flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900'>
            <AlertTriangle className='h-4 w-4 mt-0.5 shrink-0' />
            <div className='space-y-1'>
              <p className='font-medium'>
                Resolve data-quality issues before creating this dataset.
              </p>
              {unresolvedQualityNames.length > 0 && (
                <p className='text-xs text-amber-900/80'>
                  Affected files: {unresolvedQualityNames.join(', ')}.
                </p>
              )}
            </div>
          </div>
        )}

        <div className='space-y-3'>
          <div className='flex items-center gap-2'>
            <Label className='label-required'>Select CSV files</Label>
            <HelpTooltip label='Dataset file picker help'>
              <p>
                Pick the CSVs that belong in this dataset. Only files already
                uploaded to this collection appear here.
              </p>
            </HelpTooltip>
          </div>
          {csvSelectionError && (
            <p className='text-sm text-red-500'>{csvSelectionError}</p>
          )}
          {sortedOptions.length > 0 && (
            <Input
              value={filterTerm}
              onChange={(event) => setFilterTerm(event.target.value)}
              placeholder='Filter files by name'
              disabled={isSubmitting}
            />
          )}
          {sortedOptions.length === 0 ? (
            <p className='text-sm text-muted-foreground'>
              No CSV files found in this collection. Upload CSV files to create
              a dataset.
            </p>
          ) : filteredOptions.length === 0 ? (
            <p className='text-sm text-muted-foreground'>
              No files match your filter. Try a different search term.
            </p>
          ) : (
            <div className='flex flex-col gap-4 md:flex-row'>
              <div className='md:w-[320px] shrink-0 space-y-3'>
                <label className='flex items-center gap-2 text-sm text-muted-foreground cursor-pointer'>
                  <Checkbox
                    checked={selectAllState}
                    onCheckedChange={(checked) =>
                      handleSelectAllFiltered(
                        checked === true,
                        filteredOptionKeys,
                      )
                    }
                    disabled={isSubmitting}
                    aria-label='Select all filtered files'
                  />
                  <span>
                    Select all
                    {filterTerm.trim()
                      ? ` (${filteredOptions.length} filtered)`
                      : ` (${filteredOptions.length})`}
                  </span>
                </label>
                <div className='rounded-md border p-2 overflow-y-auto flex flex-col gap-2 max-h-[400px]'>
                  {filteredOptions.map((option) => {
                    const sizeLabel = formatSize(option.sizeMb);
                    const isSelected = selectedKeysSet.has(option.key);
                    const isActive = activeKey === option.key;
                    const quality = option.quality ?? null;
                    const qualityIssues = quality?.issues ?? [];
                    const issueCount =
                      quality?.issue_count ?? qualityIssues.length;
                    const requiresResolution = Boolean(
                      quality?.requires_resolution,
                    );
                    const headers = quality?.headers ?? [];

                    const handleResolveClick = (
                      event: React.MouseEvent<HTMLButtonElement>,
                    ) => {
                      event.stopPropagation();
                      if (!isSelected) {
                        handleToggle(option.key, true);
                      }
                      setActiveKey(option.key);
                    };

                    return (
                      <div
                        key={option.key}
                        className={cn(
                          'flex items-start gap-3 rounded-md border px-3 py-2 text-sm transition-colors',
                          'cursor-pointer hover:bg-muted/40',
                          isActive
                            ? 'border-ring bg-muted'
                            : isSelected
                              ? 'border-border'
                              : 'border-transparent',
                        )}
                        onClick={() => {
                          if (!isSelected) {
                            handleToggle(option.key, true);
                          }
                          setActiveKey(option.key);
                        }}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(checked) =>
                            handleToggle(option.key, checked === true)
                          }
                          disabled={isSubmitting}
                          onClick={(event) => event.stopPropagation()}
                          aria-label={`Select ${option.label}`}
                          className='mt-1'
                        />
                        <div className='flex-1 min-w-0 space-y-2'>
                          <div className='flex w-full items-start justify-between gap-3 min-w-0'>
                            <div className='min-w-0'>
                              <span className='truncate block font-medium text-foreground'>
                                {option.label}
                              </span>
                              {sizeLabel && (
                                <span className='text-xs text-muted-foreground whitespace-nowrap'>
                                  {sizeLabel}
                                </span>
                              )}
                            </div>
                            {quality && (
                              <div className='flex flex-col items-end gap-1 text-xs'>
                                <Badge
                                  className={cn(
                                    'px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide',
                                    requiresResolution
                                      ? 'bg-red-100 text-red-700'
                                      : 'bg-green-100 text-green-700',
                                  )}
                                >
                                  {requiresResolution
                                    ? 'Action needed'
                                    : 'Resolved'}
                                </Badge>
                                {issueCount > 0 && (
                                  <span className='text-muted-foreground'>
                                    {issueCount} issue
                                    {issueCount === 1 ? '' : 's'}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>

                          {quality && (
                            <div className='space-y-2 rounded-md bg-muted/40 p-2 text-xs text-muted-foreground'>
                              {qualityIssues.length ? (
                                <div className='space-y-2 max-h-[160px] overflow-y-auto pr-1'>
                                  {qualityIssues.map((issue: QualityIssue) => {
                                    const resolution =
                                      quality.resolutions?.[
                                        String(issue.column_index)
                                      ] ?? issue.resolution;
                                    const columnIndex = Number.isFinite(
                                      issue.column_index,
                                    )
                                      ? issue.column_index
                                      : undefined;
                                    const headerLabel =
                                      headers[issue.column_index - 1] ?? '';
                                    return (
                                      <div
                                        key={`${issue.type}-${issue.column_index}`}
                                        className='rounded-md border border-muted bg-background p-2 space-y-1'
                                      >
                                        <div className='flex items-center justify-between gap-2'>
                                          <span className='font-medium text-foreground truncate'>
                                            Column {columnIndex ?? '—'}{' '}
                                            {headerLabel
                                              ? `— ${headerLabel}`
                                              : headerLabel === ''
                                                ? '— (empty)'
                                                : ''}
                                          </span>
                                          <Badge
                                            className={cn(
                                              'px-2 py-0.5 text-[10px] font-medium uppercase',
                                              issue.status === 'resolved'
                                                ? 'bg-green-100 text-green-700'
                                                : 'bg-amber-100 text-amber-700',
                                            )}
                                          >
                                            {issue.status === 'resolved'
                                              ? 'Resolved'
                                              : 'Pending'}
                                          </Badge>
                                        </div>
                                        <p className='text-muted-foreground'>
                                          {issue.message || issue.type}
                                        </p>
                                        {issue.suggested_actions?.length ? (
                                          <p className='text-muted-foreground'>
                                            Suggested:{' '}
                                            {issue.suggested_actions.join(', ')}
                                          </p>
                                        ) : null}
                                        {resolution && (
                                          <p className='text-muted-foreground'>
                                            Resolution: {resolution.action}
                                            {resolution.value
                                              ? ` — ${resolution.value}`
                                              : ''}
                                          </p>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <p>No outstanding issues detected.</p>
                              )}
                              {requiresResolution && (
                                <Button
                                  type='button'
                                  size='sm'
                                  variant='link'
                                  className='h-auto px-0 self-start text-xs'
                                  onClick={handleResolveClick}
                                >
                                  Resolve issues
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className='flex-1 space-y-3'>
                <div className='flex flex-col gap-2 md:flex-row md:items-center md:justify-between'>
                  <h4 className='text-sm font-semibold'>Key configuration</h4>
                  {/* {selectedKeys.length > 1 && activeKey && (
                    <Select
                      value={activeKey}
                      onValueChange={(value) => setActiveKey(value)}
                    >
                      <SelectTrigger className='w-full justify-between md:w-[260px]'>
                        <SelectValue placeholder='Choose CSV file' />
                      </SelectTrigger>
                      <SelectContent>
                        {selectedKeyOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )} */}
                </div>

                <>
                  {activeKey ? (
                    activeSelection ? (
                      <div className='space-y-4'>
                        {activeMetadata?.quality && (
                          <CsvQualityResolver
                            key={`${activeKey}-quality`}
                            quality={activeMetadata.quality}
                            headers={activeMetadata.headers}
                            onSubmit={async (payload) =>
                              handleQualitySubmit(activeKey, payload)
                            }
                            isSubmitting={Boolean(qualitySavingKeys[activeKey])}
                          />
                        )}
                        <DatasetCsvKeyEditor
                          key={activeKey}
                          displayName={activeDisplayName}
                          metadata={activeMetadata}
                          isLoadingMetadata={Boolean(activeIsLoading)}
                          metadataError={activeMetadataError}
                          onRetryMetadata={
                            activeMetadataState?.status === 'error'
                              ? () => fetchMetadata(activeKey)
                              : undefined
                          }
                          value={activeSelection}
                          onChange={(next) =>
                            handleSelectionChange(activeKey, next)
                          }
                          datasetOptions={datasetOptions}
                          disabled={isSubmitting}
                          error={validationErrors[activeKey]}
                          showApplyToAll={selectedKeys.length > 1}
                          onApplyToAll={handleApplySelectionToAll}
                          wasAppliedToAll={lastAppliedKey === activeKey}
                        />
                      </div>
                    ) : (
                      <div className='flex h-48 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground'>
                        Loading configuration…
                      </div>
                    )
                  ) : (
                    <div className='flex h-48 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground'>
                      Select a CSV file to configure keys.
                    </div>
                  )}
                </>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
